import { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import toast from "../../utils/toast";
import { hotSectionsApi, uploadApi } from "../../services";
import "./hotSectionForm.css";

const PF = "https://backend.hoanglecf.com/images";

// ── Giá trị mặc định của 1 section (khớp schema HotSection) ──
const DEFAULT_SECTION = {
  type: "equipment",
  enabled: true,
  slug: "",
  title: "CẬP NHẬT TRANG BỊ NÓNG",
  subtitle: "",
  iconLeft: "🔥",
  iconRight: "🔥",
  align: "center",
  showUpdatedTime: false,
  updatedTimeLabel: "CẬP NHẬT LÚC",
  layout: "carousel",
  columnsDesktop: 4,
  columnsTablet: 2,
  columnsMobile: 1,
  gap: 16,
  autoplay: true,
  autoplaySpeed: 4000,
  loop: true,
  showArrows: true,
  showDots: true,
  backgroundImage: "",
  backgroundColor: "",
  overlayColor: "",
  borderColor: "",
  glowColor: "",
  radius: 16,
  padding: 24,
  titleColor: "",
  titleGradient: "",
  titleSize: 0,
  subtitleColor: "",
  accentColor: "",
  cardBackground: "",
  cardBorderColor: "",
  cardRadius: 12,
  cardTitleColor: "",
  cardSubtitleColor: "",
  cardEffect: "none",
  customCss: "",
  items: [],
};

const DEFAULT_ITEM = {
  enabled: true,
  image: "",
  title: "",
  subtitle: "Vĩnh viễn",
  description: "",
  link: "",
  openNewTab: true,
  badges: [],
  tagText: "",
  tagColor: "#ffcf5c",
  tagBackground: "transparent",
  tagBorderColor: "#ffcf5c",
  frameColor: "",
  accentColor: "",
  effect: "none",
  date: "",
  category: "",
};

const DEFAULT_BADGE = {
  text: "HOT",
  color: "#ffffff",
  background: "#e11d48",
  position: "top-left",
  animation: "none",
};

const EFFECT_OPTIONS = [
  { value: "none", label: "Không" },
  { value: "glow", label: "Hào quang (glow)" },
  { value: "float", label: "Trôi nổi (float)" },
  { value: "shine", label: "Lướt sáng (shine)" },
  { value: "zoom", label: "Phóng to khi hover (zoom)" },
  { value: "tilt", label: "Nghiêng 3D (tilt)" },
];

const BADGE_ANIM = [
  { value: "none", label: "Không" },
  { value: "pulse", label: "Nhấp nháy mềm (pulse)" },
  { value: "blink", label: "Chớp tắt (blink)" },
  { value: "shine", label: "Lướt sáng (shine)" },
  { value: "bounce", label: "Nảy (bounce)" },
];

// ── Field helpers ──
function Field({ label, hint, children }) {
  return (
    <div className="hsf-field">
      <label className="hsf-label">{label}</label>
      {children}
      {hint && <small className="hsf-hint">{hint}</small>}
    </div>
  );
}

function ColorField({ label, value, onChange, hint, allowEmpty = true }) {
  const isColor = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value || "");
  return (
    <Field label={label} hint={hint}>
      <div className="hsf-color">
        <input
          type="color"
          value={isColor ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="hsf-color-swatch"
        />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={allowEmpty ? "mặc định / màu / gradient CSS" : "#RRGGBB"}
          className="hsf-input"
        />
        {allowEmpty && value && (
          <button type="button" className="hsf-clear" onClick={() => onChange("")} title="Xoá">
            ×
          </button>
        )}
      </div>
    </Field>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="hsf-toggle">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="hsf-toggle-track"></span>
      <span className="hsf-toggle-label">{label}</span>
    </label>
  );
}

// ── Upload ảnh, trả về đường dẫn "/<filename>" ──
async function uploadImageFile(file) {
  const tempName = Date.now() + "-" + file.name;
  const res = await uploadApi.uploadFile(file, tempName);
  return `/${res?.filename || tempName}`;
}

function ImageUpload({ value, onChange, label }) {
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploading(true);
      const path = await uploadImageFile(file);
      onChange(path);
    } catch (err) {
      console.error(err);
      toast.error("Tải ảnh thất bại!");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <Field label={label}>
      <div className="hsf-image">
        {value ? (
          <div className="hsf-image-preview">
            <img src={`${PF}${value}`} alt="preview" />
            <button type="button" className="hsf-image-remove" onClick={() => onChange("")}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        ) : (
          <label className="hsf-image-drop">
            {uploading ? (
              <><i className="fas fa-spinner fa-spin"></i><span>Đang tải...</span></>
            ) : (
              <><i className="fas fa-cloud-upload-alt"></i><span>Chọn ảnh</span></>
            )}
            <input type="file" accept="image/*" hidden onChange={handle} />
          </label>
        )}
      </div>
    </Field>
  );
}

export default function HotSectionForm() {
  const { id } = useParams();
  const history = useHistory();
  const isEdit = Boolean(id);

  const [tab, setTab] = useState("content");
  const [data, setData] = useState(DEFAULT_SECTION);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        setLoadingData(true);
        const s = await hotSectionsApi.getById(id);
        setData({ ...DEFAULT_SECTION, ...s, items: (s.items || []).map((it) => ({ ...DEFAULT_ITEM, ...it })) });
      } catch (err) {
        console.error(err);
        toast.error("Không tải được dữ liệu khối!");
      } finally {
        setLoadingData(false);
      }
    })();
  }, [id, isEdit]);

  const set = useCallback((key, value) => {
    setData((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Item helpers ──
  const setItem = (index, key, value) => {
    setData((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [key]: value };
      return { ...prev, items };
    });
  };
  const addItem = () => setData((prev) => ({ ...prev, items: [...prev.items, { ...DEFAULT_ITEM }] }));
  const removeItem = (index) =>
    setData((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  const moveItem = (index, dir) => {
    const target = index + dir;
    setData((prev) => {
      if (target < 0 || target >= prev.items.length) return prev;
      const items = [...prev.items];
      const [it] = items.splice(index, 1);
      items.splice(target, 0, it);
      return { ...prev, items };
    });
  };

  // ── Badge helpers ──
  const addBadge = (itemIndex) =>
    setData((prev) => {
      const items = [...prev.items];
      items[itemIndex] = { ...items[itemIndex], badges: [...(items[itemIndex].badges || []), { ...DEFAULT_BADGE }] };
      return { ...prev, items };
    });
  const setBadge = (itemIndex, badgeIndex, key, value) =>
    setData((prev) => {
      const items = [...prev.items];
      const badges = [...(items[itemIndex].badges || [])];
      badges[badgeIndex] = { ...badges[badgeIndex], [key]: value };
      items[itemIndex] = { ...items[itemIndex], badges };
      return { ...prev, items };
    });
  const removeBadge = (itemIndex, badgeIndex) =>
    setData((prev) => {
      const items = [...prev.items];
      items[itemIndex] = {
        ...items[itemIndex],
        badges: (items[itemIndex].badges || []).filter((_, i) => i !== badgeIndex),
      };
      return { ...prev, items };
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.title.trim()) {
      toast.warning("Vui lòng nhập tiêu đề khối!");
      setTab("content");
      return;
    }
    try {
      setLoading(true);
      if (isEdit) {
        await hotSectionsApi.update(id, data);
        toast.success("Đã cập nhật khối nội dung!");
      } else {
        await hotSectionsApi.create(data);
        toast.success("Đã tạo khối nội dung!");
      }
      history.push("/hot-sections");
    } catch (err) {
      console.error(err);
      toast.error("Có lỗi khi lưu!");
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="hsf-page">
        <div className="hsf-loading">
          <i className="fas fa-spinner fa-spin"></i> <span>Đang tải...</span>
        </div>
      </div>
    );
  }

  const TABS = [
    { key: "content", label: "Nội dung", icon: "fa-heading" },
    { key: "layout", label: "Bố cục", icon: "fa-table-columns" },
    { key: "theme", label: "Giao diện", icon: "fa-palette" },
    { key: "items", label: `Item (${data.items.length})`, icon: "fa-boxes-stacked" },
    { key: "advanced", label: "Nâng cao", icon: "fa-code" },
  ];

  return (
    <div className="hsf-page">
      <form className="hsf-form" onSubmit={handleSubmit}>
        <div className="hsf-header">
          <div>
            <h1>{isEdit ? "Chỉnh sửa khối nóng" : "Tạo khối nóng mới"}</h1>
            <p>Tuỳ chỉnh toàn bộ nội dung, bố cục, màu sắc và hiệu ứng của khối</p>
          </div>
          <div className="hsf-header-actions">
            <button type="button" className="hsf-btn" onClick={() => history.push("/hot-sections")}>
              Huỷ
            </button>
            <button type="submit" className="hsf-btn hsf-btn-primary" disabled={loading}>
              {loading ? <><i className="fas fa-spinner fa-spin"></i> Đang lưu...</> : <><i className="fas fa-save"></i> {isEdit ? "Cập nhật" : "Tạo khối"}</>}
            </button>
          </div>
        </div>

        <div className="hsf-tabs">
          {TABS.map((t) => (
            <button
              type="button"
              key={t.key}
              className={`hsf-tab ${tab === t.key ? "active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              <i className={`fas ${t.icon}`}></i> {t.label}
            </button>
          ))}
        </div>

        <div className="hsf-body">
          {/* ── TAB: NỘI DUNG ── */}
          {tab === "content" && (
            <div className="hsf-panel">
              <div className="hsf-row">
                <Field label="Loại khối">
                  <select className="hsf-input" value={data.type} onChange={(e) => set("type", e.target.value)}>
                    <option value="equipment">Trang bị nóng</option>
                    <option value="news">Tin tức nóng</option>
                    <option value="custom">Tuỳ biến</option>
                  </select>
                </Field>
                <Toggle label="Hiển thị khối trên trang chủ" checked={data.enabled} onChange={(v) => set("enabled", v)} />
              </div>

              <Field label="Tiêu đề" hint="VD: CẬP NHẬT TRANG BỊ NÓNG">
                <input className="hsf-input" value={data.title} onChange={(e) => set("title", e.target.value)} />
              </Field>
              <Field label="Phụ đề" hint="VD: NHỮNG TRANG BỊ MỚI NHẤT - ĐỘC NHẤT - CHỈ CÓ TẠI HOANGLECF">
                <input className="hsf-input" value={data.subtitle} onChange={(e) => set("subtitle", e.target.value)} />
              </Field>

              <div className="hsf-row">
                <Field label="Icon trái" hint="emoji hoặc text (VD 🔥)">
                  <input className="hsf-input" value={data.iconLeft} onChange={(e) => set("iconLeft", e.target.value)} />
                </Field>
                <Field label="Icon phải" hint="emoji hoặc text">
                  <input className="hsf-input" value={data.iconRight} onChange={(e) => set("iconRight", e.target.value)} />
                </Field>
                <Field label="Canh tiêu đề">
                  <select className="hsf-input" value={data.align} onChange={(e) => set("align", e.target.value)}>
                    <option value="left">Trái</option>
                    <option value="center">Giữa</option>
                    <option value="right">Phải</option>
                  </select>
                </Field>
              </div>

              <div className="hsf-row">
                <Toggle label='Hiện đồng hồ "CẬP NHẬT LÚC"' checked={data.showUpdatedTime} onChange={(v) => set("showUpdatedTime", v)} />
                {data.showUpdatedTime && (
                  <Field label="Nhãn đồng hồ">
                    <input className="hsf-input" value={data.updatedTimeLabel} onChange={(e) => set("updatedTimeLabel", e.target.value)} />
                  </Field>
                )}
              </div>

              <Field label="Slug (tuỳ chọn)" hint="mã tham chiếu ổn định, để trống nếu không cần">
                <input className="hsf-input" value={data.slug} onChange={(e) => set("slug", e.target.value)} placeholder="vd: trang-bi-nong" />
              </Field>
            </div>
          )}

          {/* ── TAB: BỐ CỤC ── */}
          {tab === "layout" && (
            <div className="hsf-panel">
              <Field label="Kiểu bố cục">
                <select className="hsf-input" value={data.layout} onChange={(e) => set("layout", e.target.value)}>
                  <option value="carousel">Carousel (trượt ngang)</option>
                  <option value="grid">Lưới (grid)</option>
                  <option value="list">Danh sách (list)</option>
                </select>
              </Field>

              <div className="hsf-row">
                <Field label="Số cột — Desktop">
                  <input type="number" min="1" max="8" className="hsf-input" value={data.columnsDesktop} onChange={(e) => set("columnsDesktop", +e.target.value)} />
                </Field>
                <Field label="Số cột — Tablet">
                  <input type="number" min="1" max="6" className="hsf-input" value={data.columnsTablet} onChange={(e) => set("columnsTablet", +e.target.value)} />
                </Field>
                <Field label="Số cột — Mobile">
                  <input type="number" min="1" max="4" className="hsf-input" value={data.columnsMobile} onChange={(e) => set("columnsMobile", +e.target.value)} />
                </Field>
                <Field label="Khoảng cách (px)">
                  <input type="number" min="0" max="80" className="hsf-input" value={data.gap} onChange={(e) => set("gap", +e.target.value)} />
                </Field>
              </div>

              {data.layout === "carousel" && (
                <div className="hsf-subcard">
                  <h4><i className="fas fa-sliders"></i> Hành vi carousel</h4>
                  <div className="hsf-row">
                    <Toggle label="Tự động chạy" checked={data.autoplay} onChange={(v) => set("autoplay", v)} />
                    <Field label="Tốc độ tự chạy (ms)">
                      <input type="number" min="1000" step="500" className="hsf-input" value={data.autoplaySpeed} onChange={(e) => set("autoplaySpeed", +e.target.value)} />
                    </Field>
                  </div>
                  <div className="hsf-row">
                    <Toggle label="Lặp vô hạn" checked={data.loop} onChange={(v) => set("loop", v)} />
                    <Toggle label="Hiện mũi tên" checked={data.showArrows} onChange={(v) => set("showArrows", v)} />
                    <Toggle label="Hiện chấm (dots)" checked={data.showDots} onChange={(v) => set("showDots", v)} />
                  </div>
                </div>
              )}

              <div className="hsf-row">
                <Field label="Bo góc khung (px)">
                  <input type="number" min="0" max="40" className="hsf-input" value={data.radius} onChange={(e) => set("radius", +e.target.value)} />
                </Field>
                <Field label="Padding khung (px)">
                  <input type="number" min="0" max="80" className="hsf-input" value={data.padding} onChange={(e) => set("padding", +e.target.value)} />
                </Field>
              </div>
            </div>
          )}

          {/* ── TAB: GIAO DIỆN ── */}
          {tab === "theme" && (
            <div className="hsf-panel">
              <div className="hsf-subcard">
                <h4><i className="fas fa-image"></i> Nền khối</h4>
                <ImageUpload label="Ảnh nền" value={data.backgroundImage} onChange={(v) => set("backgroundImage", v)} />
                <div className="hsf-row">
                  <ColorField label="Màu nền" value={data.backgroundColor} onChange={(v) => set("backgroundColor", v)} hint="màu hoặc gradient CSS" />
                  <ColorField label="Lớp phủ (overlay)" value={data.overlayColor} onChange={(v) => set("overlayColor", v)} hint="rgba() phủ lên ảnh nền" />
                </div>
                <div className="hsf-row">
                  <ColorField label="Màu viền" value={data.borderColor} onChange={(v) => set("borderColor", v)} />
                  <ColorField label="Màu hào quang (glow)" value={data.glowColor} onChange={(v) => set("glowColor", v)} />
                </div>
              </div>

              <div className="hsf-subcard">
                <h4><i className="fas fa-heading"></i> Tiêu đề & màu nhấn</h4>
                <div className="hsf-row">
                  <ColorField label="Màu tiêu đề" value={data.titleColor} onChange={(v) => set("titleColor", v)} />
                  <ColorField label="Gradient tiêu đề" value={data.titleGradient} onChange={(v) => set("titleGradient", v)} hint="VD: linear-gradient(90deg,#ff8a00,#ffcf5c)" allowEmpty />
                </div>
                <div className="hsf-row">
                  <Field label="Cỡ tiêu đề (px, 0 = mặc định)">
                    <input type="number" min="0" max="80" className="hsf-input" value={data.titleSize} onChange={(e) => set("titleSize", +e.target.value)} />
                  </Field>
                  <ColorField label="Màu phụ đề" value={data.subtitleColor} onChange={(v) => set("subtitleColor", v)} />
                  <ColorField label="Màu nhấn (accent)" value={data.accentColor} onChange={(v) => set("accentColor", v)} hint="mũi tên, dots, viền card" />
                </div>
              </div>

              <div className="hsf-subcard">
                <h4><i className="fas fa-square"></i> Card mặc định</h4>
                <div className="hsf-row">
                  <ColorField label="Nền card" value={data.cardBackground} onChange={(v) => set("cardBackground", v)} />
                  <ColorField label="Viền card" value={data.cardBorderColor} onChange={(v) => set("cardBorderColor", v)} />
                </div>
                <div className="hsf-row">
                  <ColorField label="Màu chữ tiêu đề card" value={data.cardTitleColor} onChange={(v) => set("cardTitleColor", v)} />
                  <ColorField label="Màu chữ phụ card" value={data.cardSubtitleColor} onChange={(v) => set("cardSubtitleColor", v)} />
                  <Field label="Bo góc card (px)">
                    <input type="number" min="0" max="40" className="hsf-input" value={data.cardRadius} onChange={(e) => set("cardRadius", +e.target.value)} />
                  </Field>
                </div>
                <Field label="Hiệu ứng card mặc định" hint="áp cho mọi card, item có thể đè riêng">
                  <select className="hsf-input" value={data.cardEffect} onChange={(e) => set("cardEffect", e.target.value)}>
                    {EFFECT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          )}

          {/* ── TAB: ITEM ── */}
          {tab === "items" && (
            <div className="hsf-panel">
              <div className="hsf-items-head">
                <span>{data.items.length} item</span>
                <button type="button" className="hsf-btn hsf-btn-primary" onClick={addItem}>
                  <i className="fas fa-plus"></i> Thêm item
                </button>
              </div>

              {data.items.length === 0 && (
                <div className="hsf-empty-mini">Chưa có item nào. Bấm "Thêm item" để bắt đầu.</div>
              )}

              {data.items.map((item, i) => (
                <div key={i} className={`hsf-item ${item.enabled ? "" : "is-off"}`}>
                  <div className="hsf-item-bar">
                    <span className="hsf-item-idx">#{i + 1}</span>
                    <strong className="hsf-item-name">{item.title || "(chưa đặt tên)"}</strong>
                    <div className="hsf-item-tools">
                      <Toggle label="Hiện" checked={item.enabled} onChange={(v) => setItem(i, "enabled", v)} />
                      <button type="button" className="hsf-icon-btn" disabled={i === 0} onClick={() => moveItem(i, -1)} title="Lên"><i className="fas fa-arrow-up"></i></button>
                      <button type="button" className="hsf-icon-btn" disabled={i === data.items.length - 1} onClick={() => moveItem(i, 1)} title="Xuống"><i className="fas fa-arrow-down"></i></button>
                      <button type="button" className="hsf-icon-btn hsf-danger" onClick={() => removeItem(i)} title="Xoá"><i className="fas fa-trash"></i></button>
                    </div>
                  </div>

                  <div className="hsf-item-body">
                    <ImageUpload label="Ảnh item" value={item.image} onChange={(v) => setItem(i, "image", v)} />

                    <div className="hsf-item-fields">
                      <Field label="Tiêu đề">
                        <input className="hsf-input" value={item.title} onChange={(e) => setItem(i, "title", e.target.value)} placeholder="VD: M4A1-S DRAGON FLAME" />
                      </Field>
                      <Field label="Phụ đề">
                        <input className="hsf-input" value={item.subtitle} onChange={(e) => setItem(i, "subtitle", e.target.value)} placeholder="VD: Vĩnh viễn" />
                      </Field>
                      {data.type === "news" && (
                        <div className="hsf-row">
                          <Field label="Ngày">
                            <input className="hsf-input" value={item.date} onChange={(e) => setItem(i, "date", e.target.value)} placeholder="VD: 24/07/2026" />
                          </Field>
                          <Field label="Chuyên mục">
                            <input className="hsf-input" value={item.category} onChange={(e) => setItem(i, "category", e.target.value)} />
                          </Field>
                        </div>
                      )}
                      <Field label="Mô tả (tuỳ chọn)">
                        <textarea className="hsf-input" rows={2} value={item.description} onChange={(e) => setItem(i, "description", e.target.value)} />
                      </Field>
                      <div className="hsf-row">
                        <Field label="Link">
                          <input className="hsf-input" value={item.link} onChange={(e) => setItem(i, "link", e.target.value)} placeholder="https://..." />
                        </Field>
                        <Toggle label="Mở tab mới" checked={item.openNewTab} onChange={(v) => setItem(i, "openNewTab", v)} />
                      </div>

                      {/* Nhãn góc (VVIP) */}
                      <div className="hsf-subcard">
                        <h4><i className="fas fa-crown"></i> Nhãn góc (VD: VVIP)</h4>
                        <div className="hsf-row">
                          <Field label="Nội dung nhãn">
                            <input className="hsf-input" value={item.tagText} onChange={(e) => setItem(i, "tagText", e.target.value)} placeholder="VVIP" />
                          </Field>
                          <ColorField label="Màu chữ" value={item.tagColor} onChange={(v) => setItem(i, "tagColor", v)} />
                          <ColorField label="Màu nền" value={item.tagBackground} onChange={(v) => setItem(i, "tagBackground", v)} />
                          <ColorField label="Màu viền" value={item.tagBorderColor} onChange={(v) => setItem(i, "tagBorderColor", v)} />
                        </div>
                      </div>

                      {/* Badges */}
                      <div className="hsf-subcard">
                        <div className="hsf-subcard-head">
                          <h4><i className="fas fa-certificate"></i> Badge (MỚI / HOT...)</h4>
                          <button type="button" className="hsf-btn hsf-btn-sm" onClick={() => addBadge(i)}>
                            <i className="fas fa-plus"></i> Thêm badge
                          </button>
                        </div>
                        {(item.badges || []).map((b, bi) => (
                          <div key={bi} className="hsf-badge-row">
                            <input className="hsf-input hsf-badge-text" value={b.text} onChange={(e) => setBadge(i, bi, "text", e.target.value)} placeholder="HOT" />
                            <ColorField label="Chữ" value={b.color} onChange={(v) => setBadge(i, bi, "color", v)} allowEmpty={false} />
                            <ColorField label="Nền" value={b.background} onChange={(v) => setBadge(i, bi, "background", v)} />
                            <select className="hsf-input" value={b.position} onChange={(e) => setBadge(i, bi, "position", e.target.value)}>
                              <option value="top-left">Góc trên trái</option>
                              <option value="top-right">Góc trên phải</option>
                              <option value="bottom-left">Góc dưới trái</option>
                              <option value="bottom-right">Góc dưới phải</option>
                            </select>
                            <select className="hsf-input" value={b.animation} onChange={(e) => setBadge(i, bi, "animation", e.target.value)}>
                              {BADGE_ANIM.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                            </select>
                            <button type="button" className="hsf-icon-btn hsf-danger" onClick={() => removeBadge(i, bi)}><i className="fas fa-times"></i></button>
                          </div>
                        ))}
                      </div>

                      {/* Khung & hiệu ứng riêng */}
                      <div className="hsf-row">
                        <ColorField label="Màu khung riêng (frame)" value={item.frameColor} onChange={(v) => setItem(i, "frameColor", v)} hint="đè viền/glow của card" />
                        <ColorField label="Màu nhấn riêng" value={item.accentColor} onChange={(v) => setItem(i, "accentColor", v)} />
                        <Field label="Hiệu ứng riêng">
                          <select className="hsf-input" value={item.effect} onChange={(e) => setItem(i, "effect", e.target.value)}>
                            <option value="none">(theo card mặc định)</option>
                            {EFFECT_OPTIONS.filter((o) => o.value !== "none").map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </Field>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── TAB: NÂNG CAO ── */}
          {tab === "advanced" && (
            <div className="hsf-panel">
              <Field
                label="CSS tuỳ chỉnh (nâng cao)"
                hint='CSS được áp trong phạm vi khối này. Dùng ".hs-section" là gốc khối, ".hs-card" là card, ".hs-title" là tiêu đề... VD: .hs-card:hover { transform: scale(1.05); }'
              >
                <textarea
                  className="hsf-input hsf-code"
                  rows={16}
                  value={data.customCss}
                  onChange={(e) => set("customCss", e.target.value)}
                  placeholder={".hs-title { letter-spacing: 2px; }\n.hs-card { border-width: 2px; }"}
                  spellCheck={false}
                />
              </Field>
              <div className="hsf-note">
                <i className="fas fa-circle-info"></i>
                <span>CSS chỉ ảnh hưởng đến khối này (đã được scope theo id khối), không làm hỏng phần còn lại của trang.</span>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
